import React, { useState, useEffect } from 'react';
import { summaryApi, categoriesApi } from '../api';
import { Icon } from '../components/common/Icons';

// Client-side cache for summary stats
let cachedSummaryData = null;
let isCacheValid = false;

// Invalidate cache on mutations
if (typeof window !== 'undefined') {
  const invalidate = () => { isCacheValid = false; };
  window.addEventListener('memora:note-saved', invalidate);
  window.addEventListener('memora:doc-created', invalidate);
  window.addEventListener('memora:category-saved', invalidate);
  window.addEventListener('memora:reset-categories-view', invalidate);
}

export default function SummaryPage() {
  const [loading, setLoading] = useState(!isCacheValid || !cachedSummaryData);
  const [stats, setStats] = useState(cachedSummaryData || {
    totalNotes: 0,
    totalCategories: 0,
    trashCount: 0,
    totalWords: 0,
    averageWords: 0,
    estimatedReadTime: '0 min',
    tagsMap: {},
    categories: [],
  });

  useEffect(() => {
    let isMounted = true;

    async function fetchInsights() {
      if (isCacheValid && cachedSummaryData) {
        setStats(cachedSummaryData);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [summaryRes, categoriesRes] = await Promise.allSettled([
          summaryApi.get(),
          categoriesApi.list(),
        ]);

        if (!isMounted) return;

        const summaryData = summaryRes.status === 'fulfilled' && summaryRes.value?.data
          ? summaryRes.value.data
          : {};

        const categoriesList = categoriesRes.status === 'fulfilled' && Array.isArray(categoriesRes.value?.data)
          ? categoriesRes.value.data
          : [];

        const combinedData = {
          totalNotes: summaryData.totalNotes ?? 0,
          totalCategories: summaryData.totalCategories ?? summaryData.totalFolders ?? categoriesList.length,
          trashCount: summaryData.trashCount ?? 0,
          totalWords: summaryData.totalWords ?? 0,
          averageWords: summaryData.averageWords ?? 0,
          estimatedReadTime: summaryData.estimatedReadTime || '0 min',
          tagsMap: summaryData.tagsMap || {},
          categories: categoriesList,
        };

        cachedSummaryData = combinedData;
        isCacheValid = true;
        setStats(combinedData);
      } catch (err) {
        console.error('Failed to load summary insights', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchInsights();
    return () => { isMounted = false; };
  }, []);

  const totalCategoriesCount = stats.totalCategories;
  const uniqueTagsList = Object.entries(stats.tagsMap || {})
    .sort((a, b) => b[1] - a[1]);
  const uniqueTagsCount = uniqueTagsList.length;

  return (
    <div className="summary-page-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <Icon name="summary" size={24} />
            <span>Summary</span>
          </h1>
          <p className="page-subtitle">
            Comprehensive overview, storage distribution, and content insights across your workspace.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="loading-state-wrap">
          <div className="spinner" />
          <p className="loading-text">Analyzing workspace footprint...</p>
        </div>
      ) : (
        <div className="summary-content-stack">
          {/* Top KPI Metric Cards */}
          <div className="summary-kpi-grid">
            <div className="summary-kpi-card">
              <div className="kpi-icon-wrap accent-blue">
                <Icon name="note" size={20} />
              </div>
              <div className="kpi-details">
                <div className="kpi-metric-row">
                  <span className="kpi-value">{stats.totalNotes}</span>
                  <div className="kpi-label-stacked">
                    <span>Active</span>
                    <span>Notes</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="summary-kpi-card">
              <div className="kpi-icon-wrap accent-purple">
                <Icon name="category" size={20} />
              </div>
              <div className="kpi-details">
                <div className="kpi-metric-row">
                  <span className="kpi-value">{totalCategoriesCount}</span>
                  <div className="kpi-label-stacked">
                    <span>Total</span>
                    <span>Categories</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="summary-kpi-card">
              <div className="kpi-icon-wrap accent-emerald">
                <Icon name="sparkles" size={20} />
              </div>
              <div className="kpi-details">
                <div className="kpi-metric-row">
                  <span className="kpi-value">{uniqueTagsCount}</span>
                  <div className="kpi-label-stacked">
                    <span>Unique</span>
                    <span>Hashtags</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="summary-kpi-card">
              <div className="kpi-icon-wrap accent-amber">
                <Icon name="edit" size={20} />
              </div>
              <div className="kpi-details">
                <div className="kpi-metric-row">
                  <span className="kpi-value">{stats.totalWords.toLocaleString()}</span>
                  <div className="kpi-label-stacked">
                    <span>Words</span>
                    <span>Captured</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="summary-kpi-card">
              <div className="kpi-icon-wrap accent-rose">
                <Icon name="clock" size={20} />
              </div>
              <div className="kpi-details">
                <div className="kpi-metric-row">
                  <span className="kpi-value">{stats.estimatedReadTime}</span>
                  <div className="kpi-label-stacked">
                    <span>Reading</span>
                    <span>Time</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="summary-kpi-card">
              <div className="kpi-icon-wrap accent-red">
                <Icon name="trash" size={20} />
              </div>
              <div className="kpi-details">
                <div className="kpi-metric-row">
                  <span className="kpi-value">{stats.trashCount}</span>
                  <div className="kpi-label-stacked">
                    <span>In</span>
                    <span>Trash</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Two-Column Deep Dive Section */}
          <div className="summary-sections-grid">
            {/* Category Distribution Breakdown */}
            <div className="settings-card summary-card">
              <div className="settings-card-header">
                <div className="settings-card-icon">
                  <Icon name="category" size={18} />
                </div>
                <div className="settings-card-header-text">
                  <h2 className="settings-card-title">Category Distribution</h2>
                  <p className="settings-card-subtitle">How your notes are allocated across categories</p>
                </div>
              </div>

              {stats.categories.length === 0 ? (
                <p className="summary-empty-note">No custom categories created yet.</p>
              ) : (
                <div className="summary-distribution-list">
                  {stats.categories.map(f => {
                    const count = f.noteCount || 0;
                    const pct = stats.totalNotes > 0 ? Math.round((count / stats.totalNotes) * 100) : 0;
                    return (
                      <div key={f.id} className="distribution-row">
                        <div className="distribution-info">
                          <div className="distribution-name-wrap">
                            <Icon name="category" size={15} />
                            <span className="distribution-name">{f.name}</span>
                          </div>
                          <span className="distribution-stat">
                            {count} {count === 1 ? 'note' : 'notes'} ({pct}%)
                          </span>
                        </div>
                        <div className="distribution-progress-track">
                          <div
                            className="distribution-progress-fill"
                            style={{ width: `${Math.max(pct, count > 0 ? 4 : 0)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* HashTag Breakdown */}
            <div className="settings-card summary-card">
              <div className="settings-card-header">
                <div className="settings-card-icon">
                  <Icon name="sparkles" size={18} />
                </div>
                <div className="settings-card-header-text">
                  <h2 className="settings-card-title">HashTag Breakdown</h2>
                  <p className="settings-card-subtitle">Frequency of hashtags across your notes</p>
                </div>
              </div>

              {uniqueTagsList.length === 0 ? (
                <p className="summary-empty-note">No hashtags assigned to notes yet.</p>
              ) : (
                <div className="summary-tags-frequency-list">
                  {uniqueTagsList.slice(0, 10).map(([tag, count]) => (
                    <div key={tag} className="tag-frequency-pill">
                      <span className="tag-pill-hash">#</span>
                      <span className="tag-pill-text">{tag}</span>
                      <span className="tag-pill-count">{count}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Reading Stats & Velocity */}
              <div className="summary-submetrics-box">
                <div className="submetric-item" style={{ width: '100%' }}>
                  <span className="submetric-label">Average Note Length</span>
                  <span className="submetric-val">{stats.averageWords} words</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
